import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

// Sign-in goes through Flask, never to Logto directly: the browser posts the
// email and password to /api/auth/login, Flask checks them against Logto and
// answers with a session token it signed (backend/session.py). Every API call
// then carries that token as a Bearer header — see services/api.js.
//
// The token is kept in localStorage so a reload or a new tab stays signed in.

export const TOKEN_KEY = "adc.session";

const readToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token) => {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private mode or blocked storage: the session lasts until reload.
  }
};

const messageFrom = async (response) => {
  const body = await response.json().catch(() => null);
  return body?.message ?? `The server returned ${response.status}.`;
};

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(readToken);
  const [user, setUser] = useState(null);
  // Set when a stored token could not be checked because the server was
  // unreachable — not the same as being signed out.
  const [checkError, setCheckError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const signOut = useCallback(() => {
    writeToken(null);
    setToken(null);
    setUser(null);
    setCheckError(null);
  }, []);

  // A stored token is only trusted once the server has said who it belongs
  // to; that also brings back the current email, name and roles.
  useEffect(() => {
    if (!token || user) return;
    let active = true;
    setCheckError(null);
    fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (!active) return;
        if (response.ok) {
          setUser(await response.json());
        } else if (response.status === 401) {
          signOut();
        } else {
          setCheckError(await messageFrom(response));
        }
      })
      .catch(() => {
        if (active) setCheckError("Could not reach the server.");
      });
    return () => {
      active = false;
    };
  }, [token, user, attempt, signOut]);

  // Signing out in one tab signs out the others.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === TOKEN_KEY && !event.newValue) signOut();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [signOut]);

  // Resolves on success; throws an Error whose message the form can show.
  const signIn = useCallback(async (email, password) => {
    let response;
    try {
      response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      throw new Error("Could not reach the server. Check your connection.");
    }
    if (!response.ok) throw new Error(await messageFrom(response));
    const body = await response.json();
    writeToken(body.token);
    setToken(body.token);
    setUser(body.user);
    setCheckError(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token && user),
      isLoading: Boolean(token && !user && !checkError),
      checkError,
      retry: () => setAttempt((n) => n + 1),
      signIn,
      signOut,
    }),
    [token, user, checkError, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
